import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { buildQueryString } from '../../utils/queryStringBuilder';
import { RootState } from '../../redux/store';

// Define interface for pagination and filtering parameters
export interface FetchParams {
    page?: number;
    pageSize?: number;
    sort?: string;
    filters?: Record<string, any>;
    populate?: string;
}

interface EmployeesState {
    employees: any[];
    employee: any | null;
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        pageSize: number;
        total: number;
    };
}

const initialState: EmployeesState = {
    employees: [],
    employee: null,
    isLoading: false,
    error: null,
    pagination: {
        page: 0,
        pageSize: 10,
        total: 0
    }
};

export const fetchEmployees = createAsyncThunk(
    'employees/fetchEmployees',
    async (params: FetchParams = {}, { rejectWithValue }) => {
        try {
            // Build query string using the utility function
            const queryString = buildQueryString(params);
            
            const response = await api.get(`/api/employees${queryString}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch employees');
        }
    }
);

export const fetchEmployee = createAsyncThunk(
    'employees/fetchEmployee',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await api.get(`/api/employees/${id}?populate=*`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch employee');
        }
    }
);

export const createEmployee = createAsyncThunk(
    'employees/createEmployee',
    async (employeeData: any, { rejectWithValue }) => {
        try {
            const response = await api.post('/api/employees', { data: employeeData });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to create employee');
        }
    }
);

export const updateEmployee = createAsyncThunk(
    'employees/updateEmployee',
    async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/api/employees/${id}`, { data });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to update employee');
        }
    }
);

export const deleteEmployee = createAsyncThunk(
    'employees/deleteEmployee',
    async (id: number, { rejectWithValue }) => {
        try {
            await api.delete(`/api/employees/${id}`);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to delete employee');
        }
    }
);

const employeesSlice = createSlice({
    name: 'employees',
    initialState,
    reducers: {
        clearEmployeeError: (state) => {
            state.error = null;
        },
        clearEmployee: (state) => {
            state.employee = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Employees
            .addCase(fetchEmployees.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchEmployees.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.employees = action.payload.data;
                if (action.payload.meta && action.payload.meta.pagination) {
                    state.pagination = {
                        page: action.payload.meta.pagination.page - 1, // Convert to 0-based for frontend
                        pageSize: action.payload.meta.pagination.pageSize,
                        total: action.payload.meta.pagination.total
                    };
                }
            })
            .addCase(fetchEmployees.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Employee
            .addCase(fetchEmployee.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchEmployee.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.employee = action.payload.data;
            })
            .addCase(fetchEmployee.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Create Employee
            .addCase(createEmployee.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createEmployee.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.employees.push(action.payload.data);
            })
            .addCase(createEmployee.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Update Employee
            .addCase(updateEmployee.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateEmployee.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                const index = state.employees.findIndex(employee => employee.id === action.payload.data.id);
                if (index !== -1) {
                    state.employees[index] = action.payload.data;
                }
                state.employee = action.payload.data;
            })
            .addCase(updateEmployee.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Delete Employee
            .addCase(deleteEmployee.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteEmployee.fulfilled, (state, action: PayloadAction<number>) => {
                state.isLoading = false;
                state.employees = state.employees.filter(employee => employee.id !== action.payload);
                if (state.employee && state.employee.id === action.payload) {
                    state.employee = null;
                }
            })
            .addCase(deleteEmployee.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearEmployeeError, clearEmployee } = employeesSlice.actions;
export default employeesSlice.reducer;

// Add selector
export const selectEmployees = (state: RootState) => state.employees.employees; 